import React, { useState, useContext } from "react";
import { Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { getToken } from "../../../../api/Account";
import { tienePermiso } from "../../../../helpers/permisos";
import { DataContext } from "../../../../context/DataContext";
import { EstadoChip, useAviso } from "../../../../components/base";
import { formatearFecha } from "../../../../helpers/formato";
import { deletePermisosAsync, getPermisosAsync } from "../../../../api/Nomina";
import AddPermiso from "../permisos/AddPermiso";
import HistorialTab from "./HistorialTab";
import {
  celdaFecha,
  useConfirmacion,
  useEscritura,
  useListaRemota,
} from "../comunes";

// `isCerrado`: la nómina donde se aplicó ya se cerró.
const EstadoNomina = ({ cerrado }) =>
  cerrado ? (
    <EstadoChip estado="neutro" label="Cerrado" />
  ) : (
    <EstadoChip estado="info" label="Abierto" />
  );

const Permisos = ({ id }) => {
  const [showModal, setShowModal] = useState(false);

  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir, dialogo } = useConfirmacion();

  const {
    datos: histoyPermisos,
    cargando,
    error,
    reload: localReload,
    setReload: setLocalReload,
  } = useListaRemota(() => getPermisosAsync(token, id));

  const deletePermiso = async (permisoId) => {
    const ok = await escribir(
      () => deletePermisosAsync(token, permisoId),
      "No se pudo eliminar la ausencia"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Ausencia eliminada");
  };

  const pedirEliminar = (row) =>
    pedir({
      titulo: "Eliminar ausencia",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se eliminará la ausencia #${row.id}${row.motivo ? ` «${row.motivo}»` : ""} del ${formatearFecha(
              row.fecha
            )} (${row.horas} ${Number(row.horas) === 1 ? "hora" : "horas"}).`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Esta acción no se puede deshacer.
          </Typography>
        </>
      ),
      textoConfirmar: "Eliminar ausencia",
      peligro: true,
      onConfirmar: () => deletePermiso(row.id),
    });

  // Agregar es el Add... de EmployeesController/NominasController (NOMINA CREAR).
  const puedeAgregar = tienePermiso(access, "NOMINA CREAR");
  const puedeEliminar = tienePermiso(access, "NOMINA DELETE");

  const columnas = [
    {
      id: "id",
      etiqueta: "#",
      desde: "md",
      sx: { fontVariantNumeric: "tabular-nums" },
      render: (row) => row.id,
    },
    {
      id: "motivo",
      etiqueta: "Motivo",
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {row.motivo}
        </Typography>
      ),
    },
    {
      id: "fecha",
      etiqueta: "Fecha",
      sx: celdaFecha,
      render: (row) => formatearFecha(row.fecha),
    },
    {
      id: "horas",
      etiqueta: "Horas",
      alinear: "right",
      sx: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
      render: (row) => row.horas,
    },
    {
      id: "estado",
      etiqueta: "Nómina",
      desde: "sm",
      render: (row) => <EstadoNomina cerrado={row.isCerrado} />,
    },
  ];

  return (
    <div>
      <HistorialTab
        titulo="Ausencias"
        descripcion="Horas de ausencia que se descuentan en la nómina."
        etiquetaTabla="Historial de ausencias"
        agregar={
          puedeAgregar ? { etiqueta: "Agregar ausencia", onClick: () => setShowModal(true) } : undefined
        }
        cargando={cargando}
        error={error}
        onReintentar={() => setLocalReload(!localReload)}
        etiquetaCarga="Cargando ausencias…"
        filas={histoyPermisos}
        columnas={columnas}
        detalleMovil={(row) => <EstadoNomina cerrado={row.isCerrado} />}
        etiquetaFila={(row) => `ausencia #${row.id}`}
        vacio={{
          titulo: "Sin ausencias",
          descripcion: "Aquí aparecen las horas de ausencia del empleado.",
        }}
        acciones={(row) => [
          {
            id: "eliminar",
            etiqueta: "Eliminar",
            icono: <DeleteOutlineIcon />,
            peligro: true,
            visible: !row.isCerrado && puedeEliminar,
            onClick: () => pedirEliminar(row),
          },
        ]}
      />

      {showModal ? (
        <AddPermiso
          id={id}
          setShowModal={setShowModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      {dialogo}
    </div>
  );
};

export default Permisos;
